import { MailCheck } from 'lucide-react';
import { IntervalUnit, LBNodeData } from '@/proto/logiblocks/logiblocks';
import { CONTROL_FLOW_ICON_COLOR } from '../../styles';
import {
  type GraphData,
  type LBNodeInfo,
  NodeCategory,
  OutputType,
} from '../../types';
import { MSG_REQUIRED } from '../../validationMessages';
import { WaitForReplyCustomization } from './WaitForReplyCustomization';
import { DEFAULT_WAIT_FOR_REPLY } from './defaults';
import { buildNodeData } from '../../nodeDataUtils';
import { unitForSeconds } from '../shared/WaitDurationField';
import { secondsToAmount } from '@/pages/Logiblocks/intervalUnits';

/** Validation field names for the Wait for reply step. Shared with the UI. */
export const WaitForReplyFields = {
  WAIT: 'Wait up to',
} as const;

const UNIT_LABELS: Record<IntervalUnit, string> = {
  [IntervalUnit.SECONDS]: 'second',
  [IntervalUnit.MINUTES]: 'minute',
  [IntervalUnit.HOURS]: 'hour',
  [IntervalUnit.UNRECOGNIZED]: 'second',
};

function formatWait(data: LBNodeData): string {
  const waitData = data.wait_for_reply;
  if (!waitData?.wait_seconds) {
    return '(unconfigured)';
  }
  const unit = unitForSeconds(waitData.wait_seconds);
  const amount = secondsToAmount(waitData.wait_seconds, unit);
  const unitLabel = UNIT_LABELS[unit];
  return `Up to ${amount} ${amount === 1 ? unitLabel : `${unitLabel}s`}`;
}

export const WaitForReplyNodeInfo: LBNodeInfo = {
  protobufNodeDataKey: 'wait_for_reply',
  category: NodeCategory.CONTROL_FLOW,
  label: 'Wait for reply',
  hasInput: true,
  getIcon: (size: number) => (
    <MailCheck size={size} color={CONTROL_FLOW_ICON_COLOR} />
  ),
  outputType: OutputType.SINGLE,
  getSubtitle: (data: LBNodeData, _graph: GraphData) => formatWait(data),

  getValidationErrors: (
    _nodeId: string,
    nodeData: LBNodeData,
    _graph: GraphData
  ) => {
    if ((nodeData.wait_for_reply?.wait_seconds ?? 0) > 0) {
      return undefined;
    }
    return [
      {
        type: 'REQUIRED_FIELD',
        field: WaitForReplyFields.WAIT,
        message: MSG_REQUIRED,
      },
    ];
  },

  CustomizationComponent: WaitForReplyCustomization,
  createDefaultNodeData: () =>
    buildNodeData('wait_for_reply', DEFAULT_WAIT_FOR_REPLY),
};
